import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { ContentItem } from "@/types/content";
import { MediaPlayerHost } from "./MediaPlayerHost";
import { MediaHeader } from "./components/MediaHeader";
import { MediaInsightsTabs } from "./components/MediaInsightsTabs";

export type RecordingState = "idle" | "requesting" | "recording" | "paused" | "stopped";

interface MediaContentViewerProps {
  content: ContentItem;
  signedUrl: string | null;
  loading: boolean;
}

export function MediaContentViewer({ content, signedUrl, loading }: MediaContentViewerProps) {
  const [isPlayerCollapsed, setIsPlayerCollapsed] = useState(false);
  const [recordingState, setRecordingState] = useState<RecordingState>("idle");
  const queryClient = useQueryClient();

  // Check if this is a new recording (no storage path)
  const isNewRecording = content.type === "recording" && !content.storagePath;

  // Callback when recording is saved - invalidate queries to refresh content
  const handleRecordingComplete = useCallback(() => {
    // Invalidate content queries to refresh the content with the new storage path
    queryClient.invalidateQueries({ queryKey: ["content", content.id] });
    // Also invalidate transcript/chapters queries
    queryClient.invalidateQueries({ queryKey: ["transcript-status", content.id] });
    queryClient.invalidateQueries({ queryKey: ["chapters-status", content.id] });
  }, [queryClient, content.id]);

  // Callback to update recording state from RecordingInterface
  const handleRecordingStateChange = useCallback((state: RecordingState) => {
    setRecordingState(state);
  }, []);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        Loading media…
      </div>
    );
  }

  return (
    <MediaPlayerHost content={content} signedUrl={signedUrl}>
      {(controller, adapterProps) => (
        <div className={`flex h-full flex-col overflow-hidden p-2 ${isPlayerCollapsed ? 'gap-1' : 'gap-5'}`}>
          {/* Top block — media player or recording interface (collapsible unless new recording) */}
          <div
            className={`shrink-0 overflow-hidden transition-all duration-300 ease-in-out ${
              isPlayerCollapsed && !isNewRecording ? "h-0 opacity-0" : "h-auto opacity-100"
            }`}
          >
            <MediaHeader
              content={content}
              controller={controller}
              adapterProps={adapterProps}
              onRecordingComplete={handleRecordingComplete}
              onRecordingStateChange={handleRecordingStateChange}
            />
          </div>

          {/* Bottom block — chapters / transcripts */}
          <div className="min-h-0 flex-1">
            <MediaInsightsTabs
              content={content}
              controller={controller}
              isPlayerCollapsed={isPlayerCollapsed}
              onTogglePlayer={() => setIsPlayerCollapsed((prev) => !prev)}
              isNewRecording={isNewRecording}
              recordingState={recordingState}
            />
          </div>
        </div>
      )}
    </MediaPlayerHost>
  );
}